import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, ShieldCheck, Trash2, Sparkles, Eye, GitCompare, Loader2, Package,
  Boxes, Wifi, Activity, Bell, Settings2, X, Check, Download, Clock,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLocale } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { money, pct } from "@/lib/format";
import { RiskBadge } from "@/components/shared/Controls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CURRENCIES } from "@/lib/i18n";

const TRUST = {
  "BAIXO RISCO": "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  "RISCO MODERADO": "bg-amber-500/15 text-amber-500 border-amber-500/30",
  "ALTO RISCO": "bg-red-500/15 text-red-500 border-red-500/30",
  "DADOS INSUFICIENTES": "bg-muted text-muted-foreground border-border",
};
function TrustBadge({ level }) {
  if (!level) return <span className="text-xs text-muted-foreground">Não avaliado</span>;
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TRUST[level] || TRUST["DADOS INSUFICIENTES"]}`}>{level}</span>;
}
function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>; }

const SUP_EMPTY = { name: "", platform: "", country: "", served_countries: "", url: "", rating: "", reviews_count: "", operating_time: "", avg_processing_days: "", avg_delivery_days: "", return_policy: "", tracking_available: false, stock_available: false, notes: "" };
const PROD_EMPTY = { product: "", supplier_id: "", sku: "", category: "", price: "", currency: "USD", stock: "", shipping: "", fees: "", other_costs: "", available_countries: "", estimated_days: "", url: "", image_url: "", sale_price: "" };

export default function Suppliers() {
  const { locale } = useLocale();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("suppliers");
  const [stats, setStats] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [busy, setBusy] = useState({});
  const [compareSel, setCompareSel] = useState([]);
  const [compareRes, setCompareRes] = useState(null);
  const [comparing, setComparing] = useState(false);

  const mark = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  const loadAll = useCallback(async () => {
    const [s, sup, prod, mon] = await Promise.all([
      api.get("/suppliers/stats"), api.get("/suppliers"), api.get("/suppliers/products"), api.get("/suppliers/monitors"),
    ]);
    setStats(s.data); setSuppliers(sup.data.suppliers); setProducts(prod.data.products); setMonitors(mon.data.monitors);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (view === "integrations") api.get("/integrations").then(({ data }) => setIntegrations(data.integrations)).catch(() => {}); }, [view]);

  // ---- Supplier form ----
  const [supOpen, setSupOpen] = useState(false);
  const [supForm, setSupForm] = useState(SUP_EMPTY);
  const [savingSup, setSavingSup] = useState(false);
  const setSF = (k, v) => setSupForm((f) => ({ ...f, [k]: v }));
  const createSupplier = async (e) => {
    e.preventDefault(); setSavingSup(true);
    try {
      await api.post("/suppliers", {
        ...supForm,
        served_countries: supForm.served_countries.split(",").map((x) => x.trim()).filter(Boolean),
        rating: supForm.rating ? parseFloat(supForm.rating) : null,
        reviews_count: supForm.reviews_count ? parseInt(supForm.reviews_count) : null,
        avg_processing_days: supForm.avg_processing_days ? parseInt(supForm.avg_processing_days) : null,
        avg_delivery_days: supForm.avg_delivery_days ? parseInt(supForm.avg_delivery_days) : null,
      });
      toast.success("Fornecedor cadastrado"); setSupForm(SUP_EMPTY); setSupOpen(false); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSavingSup(false); }
  };
  const analyzeTrust = async (id) => {
    mark(`trust_${id}`, true);
    try { await api.post(`/suppliers/${id}/trust`); toast.success("Confiabilidade avaliada"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { mark(`trust_${id}`, false); }
  };
  const delSupplier = async (id) => { await api.delete(`/suppliers/${id}`); loadAll(); };

  // ---- Product form ----
  const [prodOpen, setProdOpen] = useState(false);
  const [prodForm, setProdForm] = useState(PROD_EMPTY);
  const [savingProd, setSavingProd] = useState(false);
  const setPF = (k, v) => setProdForm((f) => ({ ...f, [k]: v }));
  const createProduct = async (e) => {
    e.preventDefault();
    if (!prodForm.supplier_id) { toast.error("Selecione um fornecedor"); return; }
    setSavingProd(true);
    try {
      await api.post("/suppliers/products", {
        ...prodForm,
        price: parseFloat(prodForm.price) || 0, shipping: parseFloat(prodForm.shipping) || 0,
        fees: parseFloat(prodForm.fees) || 0, other_costs: parseFloat(prodForm.other_costs) || 0,
        sale_price: parseFloat(prodForm.sale_price) || 0,
        stock: prodForm.stock ? parseInt(prodForm.stock) : null,
        estimated_days: prodForm.estimated_days ? parseInt(prodForm.estimated_days) : null,
        available_countries: prodForm.available_countries.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Produto importado"); setProdForm(PROD_EMPTY); setProdOpen(false); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSavingProd(false); }
  };
  const analyzeProduct = async (id) => {
    mark(`opp_${id}`, true);
    try { await api.post(`/suppliers/products/${id}/analyze`); toast.success("Oportunidade analisada"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { mark(`opp_${id}`, false); }
  };
  const monitorProduct = async (id) => {
    try { const { data } = await api.post(`/suppliers/products/${id}/monitor`); toast.success(data.already ? "Já monitorado" : "Monitoramento ativado"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggleCompare = (id) => setCompareSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const runCompare = async () => {
    setComparing(true); setCompareRes(null);
    try { const { data } = await api.post("/suppliers/compare", { product_ids: compareSel, locale }); setCompareRes(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setComparing(false); }
  };

  // ---- CJ import ----
  const [cjOpen, setCjOpen] = useState(false);
  const [cjKeyword, setCjKeyword] = useState("");
  const [cjSize, setCjSize] = useState("10");
  const [cjBusy, setCjBusy] = useState(false);
  const cjImport = async (e) => {
    e.preventDefault(); setCjBusy(true);
    try {
      const { data } = await api.post("/suppliers/cj/import", { keyword: cjKeyword, size: parseInt(cjSize) || 10 });
      toast.success(`${data.imported} produto(s) importado(s) da CJdropshipping`);
      setCjOpen(false); setCjKeyword(""); loadAll(); setView("products");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setCjBusy(false); }
  };

  // ---- Integrations ----
  const [intOpen, setIntOpen] = useState(null);
  const [intCreds, setIntCreds] = useState({});
  const saveInt = async (key) => {
    try {
      await api.put(`/integrations/${key}`, { credentials: intCreds });
      toast.success("Credenciais salvas com segurança");
      setIntOpen(null); setIntCreds({});
      const { data } = await api.get("/integrations"); setIntegrations(data.integrations);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggleInt = async (key, enabled) => {
    await api.put(`/integrations/${key}`, { enabled });
    const { data } = await api.get("/integrations"); setIntegrations(data.integrations);
  };

  const VIEWS = [
    { id: "suppliers", label: "Fornecedores", icon: Boxes },
    { id: "products", label: "Produtos & Oportunidades", icon: Package },
    { id: "monitors", label: "Monitorados", icon: Activity },
    { id: "integrations", label: "Integrações", icon: Settings2 },
  ];

  return (
    <div className="space-y-6 animate-float-up">
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {[["Fornecedores", stats.total, Boxes], ["Conectados", stats.connected, Wifi], ["Em análise", stats.analyzing, Loader2], ["Produtos", stats.products, Package], ["Monitorados", stats.monitored, Activity], ["Alertas", stats.alerts, Bell]].map(([k, v, Icon]) => (
            <div key={k} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between mb-1"><span className="text-[10px] uppercase font-mono text-muted-foreground">{k}</span><Icon className="h-3.5 w-3.5 text-primary" /></div>
              <p className="font-heading text-2xl font-bold">{v}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap border-b border-border pb-3">
        {VIEWS.map((v) => (
          <button key={v.id} onClick={() => setView(v.id)} data-testid={`sup-view-${v.id}`}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${view === v.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
            <v.icon className="h-4 w-4" /> {v.label}
          </button>
        ))}
      </div>

      {/* ---------- SUPPLIERS ---------- */}
      {view === "suppliers" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground max-w-xl">Cadastre fornecedores de dropshipping. O Supplier Trust AI avalia a confiabilidade com base nos dados disponíveis.</p>
            <Dialog open={supOpen} onOpenChange={setSupOpen}>
              <DialogTrigger asChild><Button data-testid="add-supplier-btn"><Plus className="h-4 w-4 mr-1" /> Novo fornecedor</Button></DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">Novo fornecedor</DialogTitle></DialogHeader>
                <form onSubmit={createSupplier} className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><F label="Nome"><Input required value={supForm.name} onChange={(e) => setSF("name", e.target.value)} data-testid="sup-name" /></F></div>
                  <F label="Plataforma"><Input value={supForm.platform} onChange={(e) => setSF("platform", e.target.value)} placeholder="CJdropshipping, AliExpress…" data-testid="sup-platform" /></F>
                  <F label="País"><Input value={supForm.country} onChange={(e) => setSF("country", e.target.value)} placeholder="CN" /></F>
                  <F label="Países atendidos (vírgula)"><Input value={supForm.served_countries} onChange={(e) => setSF("served_countries", e.target.value)} placeholder="BR, US, EU" /></F>
                  <F label="URL"><Input value={supForm.url} onChange={(e) => setSF("url", e.target.value)} /></F>
                  <F label="Avaliação (0-5)"><Input type="number" step="0.1" value={supForm.rating} onChange={(e) => setSF("rating", e.target.value)} /></F>
                  <F label="Nº avaliações"><Input type="number" value={supForm.reviews_count} onChange={(e) => setSF("reviews_count", e.target.value)} /></F>
                  <F label="Prazo processamento (dias)"><Input type="number" value={supForm.avg_processing_days} onChange={(e) => setSF("avg_processing_days", e.target.value)} /></F>
                  <F label="Prazo entrega (dias)"><Input type="number" value={supForm.avg_delivery_days} onChange={(e) => setSF("avg_delivery_days", e.target.value)} /></F>
                  <F label="Tempo de operação"><Input value={supForm.operating_time} onChange={(e) => setSF("operating_time", e.target.value)} placeholder="Ex: 5 anos" /></F>
                  <div className="sm:col-span-2"><F label="Política de devolução"><Input value={supForm.return_policy} onChange={(e) => setSF("return_policy", e.target.value)} /></F></div>
                  <div className="flex items-center gap-2"><Switch checked={supForm.tracking_available} onCheckedChange={(v) => setSF("tracking_available", v)} /><Label className="text-xs">Rastreamento disponível</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={supForm.stock_available} onCheckedChange={(v) => setSF("stock_available", v)} /><Label className="text-xs">Estoque disponível</Label></div>
                  <div className="sm:col-span-2"><F label="Observações"><Textarea rows={2} value={supForm.notes} onChange={(e) => setSF("notes", e.target.value)} /></F></div>
                  <DialogFooter className="sm:col-span-2"><Button type="submit" disabled={savingSup} data-testid="sup-submit">{savingSup ? "Salvando…" : "Cadastrar fornecedor"}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {suppliers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">Nenhum fornecedor ainda.</div>
          ) : (
            <div className="rounded-xl border border-border bg-card divide-y divide-border">
              {suppliers.map((s) => (
                <div key={s.supplier_id} className="p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`supplier-row-${s.supplier_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap"><p className="font-semibold">{s.name}</p><TrustBadge level={s.trust_level} /></div>
                    <p className="text-xs text-muted-foreground">{s.platform || "—"} · {s.country || "?"} · {s.rating != null ? `${s.rating}★ (${s.reviews_count ?? 0})` : "sem avaliação"}</p>
                    {s.trust_analysis && <p className="text-xs text-muted-foreground mt-1 max-w-xl">{s.trust_analysis.explanation}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => analyzeTrust(s.supplier_id)} disabled={busy[`trust_${s.supplier_id}`]} data-testid={`trust-${s.supplier_id}`}>
                      {busy[`trust_${s.supplier_id}`] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Avaliar confiança
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => delSupplier(s.supplier_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------- PRODUCTS ---------- */}
      {view === "products" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground max-w-lg">Importe produtos e deixe o Opportunity AI calcular custo total, margem, risco e limite de investimento.</p>
            <div className="flex gap-2 flex-wrap">
              {compareSel.length >= 2 && <Button variant="outline" onClick={runCompare} data-testid="compare-run"><GitCompare className="h-4 w-4 mr-1" /> Comparar ({compareSel.length})</Button>}
              <Dialog open={cjOpen} onOpenChange={setCjOpen}>
                <DialogTrigger asChild><Button variant="outline" data-testid="cj-import-btn"><Download className="h-4 w-4 mr-1" /> Importar da CJ</Button></DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle className="font-heading">Importar produtos da CJdropshipping</DialogTitle></DialogHeader>
                  <form onSubmit={cjImport} className="space-y-4">
                    <p className="text-sm text-muted-foreground">Busca produtos reais via API oficial da CJ. Requer a API Key configurada no painel de Integrações (admin).</p>
                    <F label="Palavra-chave (opcional)"><Input value={cjKeyword} onChange={(e) => setCjKeyword(e.target.value)} placeholder="Ex: hoodie, watch, lamp" data-testid="cj-keyword" /></F>
                    <F label="Quantidade (máx 30)"><Input type="number" min="1" max="30" value={cjSize} onChange={(e) => setCjSize(e.target.value)} /></F>
                    <DialogFooter><Button type="submit" disabled={cjBusy} data-testid="cj-import-submit">{cjBusy ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Importando…</> : "Importar"}</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              <Dialog open={prodOpen} onOpenChange={setProdOpen}>
                <DialogTrigger asChild><Button disabled={suppliers.length === 0} data-testid="add-product-btn"><Plus className="h-4 w-4 mr-1" /> Importar produto</Button></DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-heading">Importar produto do fornecedor</DialogTitle></DialogHeader>
                  <form onSubmit={createProduct} className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2"><F label="Produto"><Input required value={prodForm.product} onChange={(e) => setPF("product", e.target.value)} data-testid="prod-name" /></F></div>
                    <F label="Fornecedor">
                      <select required className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={prodForm.supplier_id} onChange={(e) => setPF("supplier_id", e.target.value)} data-testid="prod-supplier">
                        <option value="">Selecione…</option>
                        {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
                      </select>
                    </F>
                    <F label="SKU"><Input value={prodForm.sku} onChange={(e) => setPF("sku", e.target.value)} /></F>
                    <F label="Categoria"><Input value={prodForm.category} onChange={(e) => setPF("category", e.target.value)} /></F>
                    <F label="Moeda"><select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={prodForm.currency} onChange={(e) => setPF("currency", e.target.value)} data-testid="prod-currency">{CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}</select></F>
                    <F label="Preço"><Input type="number" step="0.01" value={prodForm.price} onChange={(e) => setPF("price", e.target.value)} data-testid="prod-price" /></F>
                    <F label="Frete"><Input type="number" step="0.01" value={prodForm.shipping} onChange={(e) => setPF("shipping", e.target.value)} /></F>
                    <F label="Taxas"><Input type="number" step="0.01" value={prodForm.fees} onChange={(e) => setPF("fees", e.target.value)} /></F>
                    <F label="Outros custos"><Input type="number" step="0.01" value={prodForm.other_costs} onChange={(e) => setPF("other_costs", e.target.value)} /></F>
                    <F label="Preço de venda potencial"><Input type="number" step="0.01" value={prodForm.sale_price} onChange={(e) => setPF("sale_price", e.target.value)} data-testid="prod-sale" /></F>
                    <F label="Estoque"><Input type="number" value={prodForm.stock} onChange={(e) => setPF("stock", e.target.value)} /></F>
                    <F label="Prazo estimado (dias)"><Input type="number" value={prodForm.estimated_days} onChange={(e) => setPF("estimated_days", e.target.value)} /></F>
                    <F label="Países disponíveis (vírgula)"><Input value={prodForm.available_countries} onChange={(e) => setPF("available_countries", e.target.value)} /></F>
                    <F label="URL imagem"><Input value={prodForm.image_url} onChange={(e) => setPF("image_url", e.target.value)} /></F>
                    <DialogFooter className="sm:col-span-2"><Button type="submit" disabled={savingProd} data-testid="prod-submit">{savingProd ? "Salvando…" : "Importar produto"}</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">Nenhum produto importado. {suppliers.length === 0 && "Cadastre um fornecedor primeiro."}</div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {products.map((p) => {
                const opp = p.opportunity;
                return (
                  <div key={p.product_id} className={`rounded-xl border bg-card p-5 ${compareSel.includes(p.product_id) ? "border-primary ring-1 ring-primary/40" : "border-border"}`} data-testid={`product-card-${p.product_id}`}>
                    <div className="flex items-start gap-3">
                      {p.image_url ? <img src={p.image_url} alt="" className="h-14 w-14 rounded-lg object-cover border border-border" /> : <div className="h-14 w-14 rounded-lg bg-secondary flex items-center justify-center"><Package className="h-6 w-6 text-muted-foreground" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap"><h3 className="font-heading font-semibold">{p.product}</h3>{p.risk_level && <RiskBadge level={p.risk_level} />}</div>
                        <p className="text-xs text-muted-foreground">{p.supplier_name} · {p.category || "—"} · {p.currency}</p>
                      </div>
                      {opp?.opportunity_score != null && <div className="text-right"><p className="text-[10px] uppercase font-mono text-muted-foreground">Score</p><p className="font-heading text-xl font-bold text-primary">{opp.opportunity_score}</p></div>}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-sm">
                      <div><p className="text-[10px] text-muted-foreground">Custo total</p><p className="font-mono font-semibold">{money(p.cost_total, p.currency)}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Venda</p><p className="font-mono font-semibold">{money(p.sale_price, p.currency)}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Margem</p><p className="font-mono font-semibold">{pct(p.margin_pct)}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Prazo</p><p className="font-mono font-semibold">{p.estimated_days ?? "—"}d</p></div>
                    </div>

                    {opp && (
                      <div className="mt-3 space-y-2">
                        {opp.limit_message && <p className="text-xs font-mono text-red-500">{opp.limit_message}</p>}
                        {opp.capital_message && <p className="text-xs font-mono text-red-500">{opp.capital_message}</p>}
                        {opp.within_limit && !opp.limit_message && <p className="text-xs font-mono text-emerald-500">Dentro do limite (≈ {money(opp.cost_brl, "BRL")})</p>}
                        <p className="text-xs text-muted-foreground">{opp.score_explanation}</p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mt-4">
                      <Button size="sm" variant="secondary" onClick={() => analyzeProduct(p.product_id)} disabled={busy[`opp_${p.product_id}`]} data-testid={`analyze-prod-${p.product_id}`}>
                        {busy[`opp_${p.product_id}`] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Analisar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => monitorProduct(p.product_id)} data-testid={`monitor-${p.product_id}`}><Eye className="h-3.5 w-3.5" /> Monitorar</Button>
                      <Button size="sm" variant={compareSel.includes(p.product_id) ? "default" : "outline"} onClick={() => toggleCompare(p.product_id)} data-testid={`compare-sel-${p.product_id}`}><GitCompare className="h-3.5 w-3.5" /> Comparar</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Dialog open={!!compareRes || comparing} onOpenChange={(o) => { if (!o) { setCompareRes(null); } }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Comparação de fornecedores</DialogTitle></DialogHeader>
              {comparing ? (
                <div className="py-12 flex flex-col items-center gap-2 text-muted-foreground"><Loader2 className="h-8 w-8 animate-spin text-primary" /><p className="text-sm">Analisando fornecedores…</p></div>
              ) : compareRes && (
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2">Fornecedor</th><th>Custo total</th><th>Prazo</th><th>Margem</th><th>Risco</th></tr></thead>
                      <tbody>
                        {compareRes.rows.map((r) => (
                          <tr key={r.product_id} className="border-b border-border"><td className="py-2 font-medium">{r.supplier_name}</td><td className="font-mono">{money(r.cost_total, r.currency)}</td><td className="font-mono">{r.estimated_days ?? "—"}d</td><td className="font-mono">{pct(r.margin_pct)}</td><td><RiskBadge level={r.risk_level} /></td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div><p className="text-xs font-mono text-primary mb-1">ANÁLISE (OPPORTUNITY AI)</p><pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground bg-secondary/40 rounded-lg p-3">{compareRes.explanation}</pre></div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ---------- MONITORS ---------- */}
      {view === "monitors" && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Produtos monitorados. A IA acompanha preço, estoque, frete e margem — você recebe alertas sobre mudanças importantes.</p>
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex items-start gap-3 text-sm">
            <Clock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <p className="text-muted-foreground"><span className="font-semibold text-foreground">Robô diário ativo:</span> todo dia às 09:00 (UTC) o sistema confere automaticamente câmbio e, para produtos conectados via API (ex: CJdropshipping), preço e estoque — gerando alertas sem você precisar fazer nada.</p>
          </div>
          {monitors.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">Nenhum produto monitorado.</div>
          ) : (
            <div className="rounded-xl border border-border bg-card divide-y divide-border">
              {monitors.map((m) => (
                <div key={m.monitor_id} className="p-4 flex items-center justify-between">
                  <div><p className="font-medium">{m.product}</p><p className="text-xs text-muted-foreground">{m.supplier_name} · base {money(m.baseline_price)} · margem {pct(m.baseline_margin)}</p></div>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => { await api.delete(`/suppliers/monitors/${m.monitor_id}`); loadAll(); }}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------- INTEGRATIONS ---------- */}
      {view === "integrations" && (
        <div className="space-y-4">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-muted-foreground">
            {isAdmin ? "Conecte as APIs de fornecedores uma por uma. As credenciais são armazenadas com segurança no backend e nunca expostas no frontend." : "Apenas administradores podem configurar integrações. As credenciais nunca são expostas."}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {integrations.map((it) => (
              <div key={it.key} className="rounded-xl border border-border bg-card p-4" data-testid={`integration-${it.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold">{it.name}</p>
                    <span className={`text-xs font-mono ${it.configured ? "text-emerald-500" : "text-amber-500"}`}>{it.status}</span>
                    {it.key === "cjdropshipping" && isAdmin && <p className="text-[11px] text-muted-foreground mt-1">API Key: formato <span className="font-mono">CJUserNum@api@token</span> (gere em developers.cjdropshipping.com → Apps).</p>}
                  </div>
                  {isAdmin && it.category === "supplier" && (
                    <div className="flex items-center gap-2">
                      <Switch checked={it.enabled} onCheckedChange={(v) => toggleInt(it.key, v)} data-testid={`int-toggle-${it.key}`} />
                      <Button size="sm" variant="outline" onClick={() => { setIntOpen(it.key); setIntCreds({}); }} data-testid={`int-config-${it.key}`}>Configurar</Button>
                    </div>
                  )}
                </div>
                {intOpen === it.key && isAdmin && (
                  <div className="mt-4 pt-4 border-t border-border space-y-3">
                    {["api_key", "api_secret", "access_token", "api_url"].map((field) => (
                      <F key={field} label={field.replace("_", " ").toUpperCase()}>
                        <Input placeholder={it.credentials?.[field] || ""} onChange={(e) => setIntCreds((c) => ({ ...c, [field]: e.target.value }))} data-testid={`int-${it.key}-${field}`} />
                      </F>
                    ))}
                    <div className="flex gap-2"><Button size="sm" onClick={() => saveInt(it.key)} data-testid={`int-save-${it.key}`}><Check className="h-3.5 w-3.5 mr-1" /> Salvar</Button><Button size="sm" variant="ghost" onClick={() => setIntOpen(null)}>Cancelar</Button></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
